<script setup>
import AppPagination from '../../../shared/components/AppPagination.vue'
import ReportTemplateSelect from '../../../report/components/ReportTemplateSelect.vue'
import {
  calculateHistoryNumber,
  canDownloadPdf,
  formatHistoryDate,
  getAnalysisStatusLabel
} from '../historyUtils.js'

defineProps({
  results: { type: Array, required: true },
  page: { type: Number, required: true },
  pageSize: { type: Number, required: true },
  totalElements: { type: Number, required: true },
  totalPages: { type: Number, required: true },
  isLoading: { type: Boolean, default: false },
  downloadingReportIds: { type: Set, required: true },
  templates: { type: Array, required: true },
  selectedTemplateIds: { type: Object, required: true },
  templateErrors: { type: Object, default: () => ({}) },
  isTemplateLoading: { type: Boolean, default: false },
  showAdminDetails: { type: Boolean, default: false }
})

defineEmits(['download', 'page-change', 'template-change'])
</script>

<template>
  <div class="history-table-wrap" :aria-busy="isLoading">
    <table class="history-table" :class="{ 'is-admin': showAdminDetails }">
      <thead>
        <tr>
          <th>번호</th>
          <th>산출일시</th>
          <th>사용자</th>
          <th v-if="showAdminDetails">이메일</th>
          <th v-if="showAdminDetails">전화 뒷자리</th>
          <th>고객명</th>
          <th>고유번호</th>
          <th>상태</th>
          <th>리포트 종류 · PDF 받기</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="isLoading && results.length === 0">
          <td :colspan="showAdminDetails ? 9 : 7" class="history-empty">결과이력을 불러오는 중입니다.</td>
        </tr>
        <tr v-else-if="results.length === 0">
          <td :colspan="showAdminDetails ? 9 : 7" class="history-empty">아직 산출된 결과가 없습니다. 첫 분석을 시작해 보세요.</td>
        </tr>
        <template v-else>
          <tr v-for="(result, index) in results" :key="result.uniqueNumber">
            <td>{{ calculateHistoryNumber(totalElements, page, pageSize, index) }}</td>
            <td class="history-date">{{ formatHistoryDate(result.createdAt) }}</td>
            <td>{{ result.userName || '-' }}</td>
            <td v-if="showAdminDetails">{{ result.email || '-' }}</td>
            <td v-if="showAdminDetails">{{ result.phoneLast4 || '-' }}</td>
            <td class="history-customer">{{ result.customerName || '-' }}</td>
            <td class="history-unique-number" :title="result.uniqueNumber">{{ result.uniqueNumber }}</td>
            <td><span class="status-pill" :class="`is-${result.status?.toLowerCase()}`"><i aria-hidden="true" />{{ getAnalysisStatusLabel(result.status) }}</span></td>
            <td class="history-pdf-cell">
              <div class="history-pdf-actions">
                <ReportTemplateSelect
                  :model-value="selectedTemplateIds[result.reportId]"
                  :templates="templates"
                  :loading="isTemplateLoading"
                  :disabled="isTemplateLoading || !canDownloadPdf(result) || downloadingReportIds.has(result.reportId)"
                  label="리포트 종류"
                  compact
                  @update:model-value="$emit('template-change', result.reportId, $event)"
                />
                <button
                  class="history-download-button"
                  type="button"
                  :disabled="isTemplateLoading || !canDownloadPdf(result) || !selectedTemplateIds[result.reportId] || downloadingReportIds.has(result.reportId)"
                  @click="$emit('download', result)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11" /><path d="M7 10l5 5 5-5" /><path d="M4 19h16" /></svg>
                  {{ downloadingReportIds.has(result.reportId) ? '만드는 중...' : 'PDF 받기' }}
                </button>
              </div>
              <p v-if="templateErrors[result.reportId]" class="history-row-error" role="alert">{{ templateErrors[result.reportId] }}</p>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>

  <!-- 모바일: 카드 목록 (CSS 에서 820px 이하만 보임) -->
  <ul class="history-cards" :aria-busy="isLoading">
    <li v-if="results.length === 0" class="history-card-empty">{{ isLoading ? '결과이력을 불러오는 중입니다.' : '아직 산출된 결과가 없습니다.' }}</li>
    <li v-for="(result, index) in results" :key="`card-${result.uniqueNumber}`" class="history-card">
      <div class="history-card-head">
        <b>{{ calculateHistoryNumber(totalElements, page, pageSize, index) }}. {{ result.customerName || '-' }}</b>
        <span class="status-pill" :class="`is-${result.status?.toLowerCase()}`"><i aria-hidden="true" />{{ getAnalysisStatusLabel(result.status) }}</span>
      </div>
      <dl>
        <dt>산출일시</dt><dd>{{ formatHistoryDate(result.createdAt) }}</dd>
        <dt>사용자</dt><dd>{{ result.userName || '-' }}</dd>
        <template v-if="showAdminDetails"><dt>이메일</dt><dd>{{ result.email || '-' }}</dd><dt>전화 뒷자리</dt><dd>{{ result.phoneLast4 || '-' }}</dd></template>
        <dt>고유번호</dt><dd>{{ result.uniqueNumber || '-' }}</dd>
      </dl>
      <ReportTemplateSelect
        :model-value="selectedTemplateIds[result.reportId]"
        :templates="templates"
        :loading="isTemplateLoading"
        :disabled="isTemplateLoading || !canDownloadPdf(result) || downloadingReportIds.has(result.reportId)"
        label="리포트 종류"
        @update:model-value="$emit('template-change', result.reportId, $event)"
      />
      <button class="history-download-button" type="button" :disabled="isTemplateLoading || !canDownloadPdf(result) || !selectedTemplateIds[result.reportId] || downloadingReportIds.has(result.reportId)" @click="$emit('download', result)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11" /><path d="M7 10l5 5 5-5" /><path d="M4 19h16" /></svg>
        {{ downloadingReportIds.has(result.reportId) ? 'PDF 만드는 중...' : 'PDF 받기' }}
      </button>
      <p v-if="templateErrors[result.reportId]" class="history-row-error" role="alert">{{ templateErrors[result.reportId] }}</p>
    </li>
  </ul>

  <AppPagination
    :page="page"
    :total-pages="totalPages"
    :disabled="isLoading"
    @change="$emit('page-change', $event)"
  />
</template>
